import type { StudySelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { ChevronRightIcon, EditIcon, SearchIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { MeiaSearchPalette } from './meia-search-palette.component';

type MeiaStudyTopHeaderProps = {
  /** Reserved slot for the ACR shield (AZ-454). Empty in AZ-453. */
  acrSlot?: ReactNode;
  //
  canEdit?: boolean;
  onEdit?: () => void;
  phaseLabel?: string;
  study: StudySelectDto;
};

export const MeiaStudyTopHeader: FC<MeiaStudyTopHeaderProps> = (props) => {
  const { acrSlot, canEdit, onEdit, phaseLabel, study } = props;
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;

      // Don't hijack ⌘K while the user is typing in a field (input/textarea/contenteditable).
      const target = event.target;
      const isEditableTarget =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isEditableTarget) return;

      event.preventDefault();
      setIsSearchOpen((open) => !open);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="flex w-full items-center justify-between gap-3 pb-2">
      <nav
        aria-label="Breadcrumb"
        className="flex min-w-0 items-center gap-1 truncate text-xs text-gray-500"
      >
        <Link
          className="hover:text-gray-900"
          to={meiaRoute.studies}
        >
          Studies
        </Link>
        <ChevronRightIcon className="size-3 shrink-0 text-gray-400" />
        {phaseLabel ? (
          <>
            <Link
              className="truncate text-gray-700 hover:text-gray-900"
              to={meiaRoute.studyOverview(study.id)}
            >
              {study.name}
            </Link>
            <ChevronRightIcon className="size-3 shrink-0 text-gray-400" />
            <span className="shrink-0">{phaseLabel}</span>
          </>
        ) : (
          <span className="truncate text-gray-700">{study.name}</span>
        )}
      </nav>

      <div className="flex shrink-0 items-center gap-2">
        <button
          aria-label="Search studies, threads, and tasks"
          className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-700"
          onClick={() => setIsSearchOpen(true)}
          type="button"
        >
          <SearchIcon className="size-4" />
          <span>Search</span>
          <kbd className="ml-1 rounded border border-gray-200 bg-gray-50 px-1 font-sans text-[10px] text-gray-400">
            ⌘K
          </kbd>
        </button>
        {acrSlot}
        {canEdit && onEdit && (
          <IconButton
            ariaLabel="Edit Study"
            icon={<EditIcon />}
            onClick={onEdit}
            size="md"
            variant="outline"
          />
        )}
      </div>

      <MeiaSearchPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </div>
  );
};
